// Rota Draft review page - shows every drafted shift inside a week and lets
// the manager move the period through the lifecycle:
//   awaiting_review -> approved -> published
// Approve is a pure state change (does NOT push to live rota).
// Publish is the explicit "push to live rota" step.
// Auto-publish (next phase) will sit on top of the same Publish endpoint
// when enabled by the customer.

import { useEffect, useState, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import EmployeeSkillsLine from "@/components/EmployeeSkillsLine";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Loader2, ChevronLeft, CheckCircle2, XCircle, RefreshCw, AlertTriangle,
  Send, Undo2, CalendarDays, Pencil, Trash2, Coffee,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter, DialogClose,
} from "@/components/ui/dialog";

const BAND_TONES = {
  strong: "text-emerald-800 bg-emerald-50 border-emerald-200",
  good:   "text-blue-800 bg-blue-50 border-blue-200",
  fair:   "text-amber-900 bg-amber-50 border-amber-200",
};

const STATUS_TONES = {
  awaiting_review:   "text-amber-900 bg-amber-50 border-amber-200",
  approved:          "text-blue-800 bg-blue-50 border-blue-200",
  published:         "text-emerald-800 bg-emerald-50 border-emerald-200",
  discarded:         "text-slate-600 bg-slate-50 border-slate-200",
  superseded:        "text-slate-600 bg-slate-50 border-slate-200",
};

const CELL_STATUS_TONES = {
  pending_review:    "text-amber-900 bg-amber-50 border-amber-200",
  approved_in_draft: "text-blue-800 bg-blue-50 border-blue-300",
  applied:           "text-emerald-800 bg-emerald-50 border-emerald-200",
  rejected:          "text-slate-600 bg-slate-50 border-slate-200",
  superseded:        "text-slate-500 bg-slate-50 border-slate-200",
};

function fmtDate(iso) {
  try {
    return new Date(iso + "T00:00:00Z").toLocaleDateString("en-GB",
      { weekday: "short", day: "numeric", month: "short" });
  } catch (e) { return iso; }
}

// Phase 2Z.al - pure read-only helper. Computes the per-assignment
// timeline that the publish path will actually write. Render-only;
// never calls an API, never writes data, never reaches the backend.
// Mirrors `_rsb_split_publish_blocks` (`server.py`:15608) exactly:
//   - If EVERY row in the block has a positive integer
//     `override_minutes` AND their sum equals the block's net
//     minutes (gross - break), honour the overrides verbatim.
//   - Otherwise equal-split with remainder distributed to the first
//     rows (the unique production behaviour for legacy cells).
// Break placement is honest:
//   - If `break_start_time` is present, the break is rendered with
//     exact start/end times sitting between assignment rows.
//   - If `break_start_time` is missing, the break is still rendered
//     as a row but WITHOUT timing - copy reads "no fixed time".
// Output rows are intended for direct rendering and carry the kind
// flag so the view layer can style assignment vs break differently.
export function _t2m(t) {
  if (!t || typeof t !== "string") return null;
  const [h, m] = t.split(":");
  const n = parseInt(h, 10) * 60 + parseInt(m, 10);
  return Number.isFinite(n) ? n : null;
}
export function _m2t(n) {
  if (typeof n !== "number" || !Number.isFinite(n)) return null;
  const total = ((Math.round(n) % 1440) + 1440) % 1440;
  const h = Math.floor(total / 60);
  const m = total % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}
export function fmtMinutes(mins) {
  if (typeof mins !== "number" || !Number.isFinite(mins) || mins <= 0) {
    return "0 min";
  }
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h && m) return `${mins} min (${h}h ${m}m)`;
  if (h) return `${mins} min (${h}h)`;
  return `${mins} min`;
}
export function computeAssignmentTimeline(blockCells) {
  if (!Array.isArray(blockCells) || blockCells.length === 0) {
    return { rows: [], block_net: 0, used_overrides: false };
  }
  const first = blockCells[0] || {};
  const hasWindow = Boolean(first.start_time && first.end_time);
  const gross = hasWindow
    ? Math.max(0, (_t2m(first.end_time) || 0) - (_t2m(first.start_time) || 0))
    : 480;
  const brk = Math.max(0, Number(first.break_minutes) || 0);
  const blockNet = (brk && brk < gross) ? (gross - brk) : gross;
  const breakStartMin = first.break_start_time
    ? _t2m(first.break_start_time)
    : null;
  // Honour overrides only when every row has a positive integer and
  // their sum equals block net. This is the same gate the backend
  // uses, so the preview shows exactly what publish will write.
  const overrides = blockCells.map((c) => {
    const m = c?.override_minutes;
    return (typeof m === "number" && m > 0) ? m : null;
  });
  const allOverridden = overrides.every((m) => typeof m === "number" && m > 0)
    && overrides.reduce((a, b) => a + b, 0) === blockNet;
  let perRow;
  if (allOverridden) {
    perRow = overrides;
  } else {
    const n = blockCells.length;
    const base = Math.floor(blockNet / n);
    const rem = blockNet % n;
    perRow = blockCells.map((_, i) => base + (i < rem ? 1 : 0));
  }
  const rows = [];
  let cursor = hasWindow ? _t2m(first.start_time) : null;
  let breakInserted = !(brk > 0);
  for (let i = 0; i < blockCells.length; i += 1) {
    const c = blockCells[i];
    const mins = perRow[i];
    const label = c?.target_category_name
      || c?.policy_name
      || "Assignment";
    if (cursor === null) {
      // Full-day cell - report duration only, no timing.
      rows.push({
        kind: "assignment",
        cell: c,
        label,
        start: null,
        end: null,
        mins,
      });
      if (!breakInserted && i === 0) {
        rows.push({
          kind: "break",
          mins: brk,
          start: null,
          end: null,
          has_fixed_time: false,
        });
        breakInserted = true;
      }
      continue;
    }
    const segStart = cursor;
    const segEnd = cursor + mins;
    if (!breakInserted) {
      // Phase 2Z.al.3 - place the break at the user's explicit
      // `break_start_time` EXACTLY when set. Matches the editor's
      // `_sequenceFor` behaviour: the assignment that contains the
      // break may visually span past the break (extended segment
      // end), but the break row itself is at the user-supplied time.
      // Previous behaviour inserted the break at the segment's
      // mins-boundary, which silently disagreed with the editor.
      if (breakStartMin !== null
          && breakStartMin >= segStart
          && breakStartMin < segEnd + brk) {
        // The segment extends to absorb the break (so its end-time
        // matches what the editor shows). The break row uses the
        // user-supplied start/end exactly.
        const extendedEnd = segEnd + brk;
        rows.push({
          kind: "assignment",
          cell: c,
          label,
          start: _m2t(segStart),
          end: _m2t(extendedEnd),
          mins,
        });
        rows.push({
          kind: "break",
          mins: brk,
          start: _m2t(breakStartMin),
          end: _m2t(breakStartMin + brk),
          has_fixed_time: true,
        });
        cursor = extendedEnd;
        breakInserted = true;
        continue;
      }
      if (breakStartMin === null && i === 0) {
        // No explicit break time and at least one break configured.
        // The publish path effectively places the break right after
        // the first assignment's share, so we surface those times
        // here as the system's auto-placement. `has_fixed_time`
        // stays false so the renderer can label the times "auto".
        rows.push({
          kind: "assignment",
          cell: c,
          label,
          start: _m2t(segStart),
          end: _m2t(segEnd),
          mins,
        });
        rows.push({
          kind: "break",
          mins: brk,
          start: _m2t(segEnd),
          end: _m2t(segEnd + brk),
          has_fixed_time: false,
        });
        cursor = segEnd + brk;
        breakInserted = true;
        continue;
      }
    }
    rows.push({
      kind: "assignment",
      cell: c,
      label,
      start: _m2t(segStart),
      end: _m2t(segEnd),
      mins,
    });
    cursor = segEnd;
  }
  return { rows, block_net: blockNet, used_overrides: allOverridden };
}

// Groups draft cells for one day into Rota Style blocks. Cells that
// share a `style_cell_id` belong to the same block and render as a
// timeline. Cells without a `style_cell_id` (legacy / non-Rota-Style
// drafts) are returned as their own singleton blocks, so the layout
// is byte-identical to today for those rows.
export function groupCellsIntoBlocks(cellsForDay) {
  if (!Array.isArray(cellsForDay) || cellsForDay.length === 0) return [];
  const groups = new Map();
  const singletonOrder = [];
  for (const c of cellsForDay) {
    const k = c?.style_cell_id
      ? `s:${c.style_cell_id}`
      : `x:${c?.draft_id || singletonOrder.length}`;
    if (!groups.has(k)) {
      groups.set(k, []);
      singletonOrder.push(k);
    }
    groups.get(k).push(c);
  }
  return singletonOrder.map((k) => groups.get(k));
}

export default function DraftsRotaReviewPage() {
  const { period_id } = useParams();
  const navigate = useNavigate();
  // TP-RS.team.1 -- role-gate the selected-team publish UI.
  const { user } = useAuth();
  const isCompanyAdmin = user?.role === "company_admin" || user?.role === "super_admin";
  const [period, setPeriod] = useState(null);
  const [autoPublish, setAutoPublish] = useState(null);  // {enabled, publish_cooling_off_hours}
  // Phase 2Z.ah - auto-approve config so the awaiting_review banner
  // can tell the manager "if you do nothing this draft will be
  // auto-approved by automation in about N hours". Reuses the
  // existing config endpoint; no new backend route. Auto-approve only
  // ever runs on drafts triggered by scheduler/system (server.py
  // L16380), so the banner is gated on triggered_by too - manual
  // drafts never see this banner because they never auto-approve.
  const [autoApprove, setAutoApprove] = useState(null);  // {enabled, review_window_hours}
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [publishConfirmOpen, setPublishConfirmOpen] = useState(false);
  // TP-RS.team.1 -- Workspace Admin selected-team publish. UI-only:
  // hits the same /drafts/rota/{id}/publish endpoint with the
  // existing ?team_id= query (TP-3 already accepts company_admin
  // on this path). Default "all" preserves the legacy full-publish
  // behaviour. Visible to company_admin / super_admin only; Team
  // Admin keeps the existing partial-publish button elsewhere.
  const [publishScope, setPublishScope] = useState("all");
  // Phase 2Z.ae - Approve and publish shortcut + skills lookup.
  // The shortcut is purely a UI sequencer: approve then publish, both
  // via existing endpoints. Skills are fetched once via the existing
  // /skills endpoint to power EmployeeSkillsLine under each cell.
  const [approvePublishConfirmOpen, setApprovePublishConfirmOpen] = useState(false);
  // Phase 2Z.ak - Discard draft is a manager-facing safety hatch for
  // a draft that should not have been generated (e.g. the manager
  // generated one manually, automation will generate a better one
  // later). It calls the existing /drafts/rota/{id}/discard endpoint,
  // which only moves the period and its pending cells to status
  // 'discarded'. The automation gate (`_existing_period_for_window`)
  // only blocks on 'awaiting_review' / 'approved', so future
  // automation can generate a fresh draft for the same week. Live
  // allocations are never touched by the discard endpoint.
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [empSkillIdsById, setEmpSkillIdsById] = useState({});
  const [skillNameById, setSkillNameById] = useState({});
  // Phase 2Z.al.2 - team name lookup for the Team filter. Map id ->
  // name; "all" sentinel for no filter. The cell-level `target_team_id`
  // is already on every draft_allocation row (generator writes it at
  // server.py:15213), so this filter is purely frontend.
  const [teamNameById, setTeamNameById] = useState({});
  const [empTeamIdById, setEmpTeamIdById] = useState({});
  const [selectedTeamId, setSelectedTeamId] = useState("all");
  // Grid view groups cells into an employee × day matrix so the
  // manager can sign off by day or by employee. List view keeps the
  // existing per-day vertical list. The grid is the new default for
  // Rota-Style drafts where the pattern is dense enough to scan.
  const [viewMode, setViewMode] = useState("grid");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data }, ap, aa, emps, skills, teams] = await Promise.all([
        api.get(`/drafts/rota/${period_id}`),
        api.get(`/automation/auto-publish-drafts/config`).catch(() => ({ data: null })),
        api.get(`/automation/auto-approve-drafts/config`).catch(() => ({ data: null })),
        api.get(`/employees`).catch(() => ({ data: [] })),
        api.get(`/skills?include_archived=false`).catch(() => ({ data: [] })),
        api.get(`/teams`).catch(() => ({ data: [] })),
      ]);
      setPeriod(data);
      setAutoPublish(ap?.data || null);
      setAutoApprove(aa?.data || null);
      const empMap = {};
      const empTeam = {};
      for (const e of (emps.data || [])) {
        empMap[e.employee_id] = e.skill_ids || [];
        if (e.team_id) empTeam[e.employee_id] = e.team_id;
      }
      setEmpSkillIdsById(empMap);
      setEmpTeamIdById(empTeam);
      const sMap = {};
      for (const s of (skills.data || [])) sMap[s.skill_id] = s.name;
      setSkillNameById(sMap);
      // Phase 2Z.al.2 - build the team-name lookup for the filter.
      // No new endpoint - /teams is already exposed and used across
      // the app. Cells filter by `target_team_id` (set by the
      // generator) with a fallback to the employee's home team.
      const tMap = {};
      for (const t of (teams.data || [])) tMap[t.team_id] = t.name;
      setTeamNameById(tMap);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Could not load draft");
    } finally {
      setLoading(false);
    }
  }, [period_id]);

  useEffect(() => { load(); }, [load]);

  const cancelAutoPublish = useCallback(async () => {
    if (!window.confirm("Cancel the auto-publish for this rota? It will stay approved so you can edit or publish it manually.")) return;
    setBusy(true);
    try {
      await api.post(`/drafts/rota/${period_id}/cancel-auto-publish`);
      toast.success("Auto-publish cancelled. The rota stays approved.");
      await load();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Cancel failed");
    } finally { setBusy(false); }
  }, [period_id, load]);

  const approveDraft = useCallback(async () => {
    setBusy(true);
    try {
      await api.post(`/drafts/rota/${period_id}/approve`, { confirm: true });
      toast.success("All pending shifts approved. Ready to publish to the live rota.");
      await load();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Approve failed");
    } finally { setBusy(false); }
  }, [period_id, load]);

  // Phase 2Z V1 — "Edit draft in roster": lands the generated draft
  // into the existing roster draft for that exact week so the admin
  // can edit with bulk tools, then redirects to /app/roster?week=XX.
  // Reuses /drafts/rota/{id}/publish?as_draft=true. NO employee email
  // is sent. RSA auto-publish later completes the final publish.
  const editInRoster = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.post(
        `/drafts/rota/${period_id}/publish?as_draft=true`,
        { confirm: true });
      const wk = data?.week_start;
      toast.success("Draft moved into the roster. Edit it here, then publish when ready.");
      navigate(`/app/roster?week=${encodeURIComponent(wk)}`);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Edit in roster failed");
      setBusy(false);
    }
  }, [period_id, navigate]);

  // Approve a subset (one whole day, or all of one employee's
  // shifts) without moving the period. The period stays in
  // awaiting_review until the manager clicks "Approve all into
  // draft" or every pending cell has been hand-approved.
  const approveSubset = useCallback(async (filter, label) => {
    setBusy(true);
    try {
      const { data } = await api.post(
        `/drafts/rota/${period_id}/approve-cells`,
        { confirm: true, ...filter });
      const flipped = data?.cells_flipped || 0;
      if (flipped === 0) {
        toast.info(`No pending shifts found for ${label}.`);
      } else {
        toast.success(
          `Approved ${flipped} shift${flipped === 1 ? "" : "s"} for ${label}` +
          ` into the draft. ${data?.remaining_pending || 0} still pending.`);
      }
      await load();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Approve failed");
    } finally { setBusy(false); }
  }, [period_id, load]);

  const publishToLive = useCallback(async () => {
    setBusy(true);
    setPublishConfirmOpen(false);
    try {
      // TP-RS.team.1 -- when a Workspace Admin picks a specific team
      // from the publish dialog selector, forward ?team_id= so the
      // existing TP-3 partial-publish branch fires (TP-4 invariants
      // preserved). Default "all" omits the param and hits the legacy
      // full-publish branch byte-stably.
      const url = publishScope === "all"
        ? `/drafts/rota/${period_id}/publish`
        : `/drafts/rota/${period_id}/publish?team_id=${encodeURIComponent(publishScope)}`;
      const { data } = await api.post(url, { confirm: true });
      if (data.failed_count > 0) {
        toast.warning(`Published ${data.applied_count} shifts. ${data.failed_count} could not be applied.`);
      } else {
        toast.success(`Published. ${data.applied_count} shifts are now live.`);
      }
      // Reset the scope selector so a follow-up publish opens with
      // the safe default again.
      setPublishScope("all");
      await load();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Publish failed");
    } finally { setBusy(false); }
  }, [period_id, load, publishScope]);

  // Phase 2Z.ae - manual UI shortcut: Approve and publish to live rota.
  // Pure sequencer over the existing two endpoints:
  //   1. POST /drafts/rota/{id}/approve  ({confirm: true})
  //   2. POST /drafts/rota/{id}/publish  ({confirm: true})
  // The two endpoints / approval state / publish state / automation /
  // RSA logic / cooling-off / auto-publish / auto-draft are all
  // untouched. If approve succeeds and publish fails, the draft is
  // left in the safe existing "approved" state and the user sees a
  // clear error; no duplicate live rota is created because the
  // publish endpoint is the only writer to the live rota.
  const approveAndPublish = useCallback(async () => {
    setBusy(true);
    setApprovePublishConfirmOpen(false);
    let approveOk = false;
    try {
      await api.post(`/drafts/rota/${period_id}/approve`, { confirm: true });
      approveOk = true;
      // TP-RS.team.1 - honour the shared `publishScope` selector so
      // the Approve-and-publish shortcut respects selected-team
      // publish too. Default "all" keeps the legacy full-publish.
      const publishUrl = publishScope === "all"
        ? `/drafts/rota/${period_id}/publish`
        : `/drafts/rota/${period_id}/publish?team_id=${encodeURIComponent(publishScope)}`;
      const { data } = await api.post(publishUrl, { confirm: true });
      if (data.failed_count > 0) {
        toast.warning(`Approved and published ${data.applied_count} shifts. ${data.failed_count} could not be applied.`);
      } else {
        toast.success(`Approved and published. ${data.applied_count} shifts are now live.`);
      }
      // Phase 2Z.af - on successful publish, take the manager to the
      // Roster page for that week so they can see the shifts in
      // context. Falls back to a refresh in place if window_start is
      // unavailable for any reason. Uses the existing /app/roster?week=
      // deep-link route. Does not change publish behaviour.
      const wk = period?.window_start;
      if (wk) {
        navigate(`/app/roster?week=${encodeURIComponent(wk)}`);
      } else {
        await load();
      }
    } catch (err) {
      const msg = formatError(err.response?.data?.detail);
      if (approveOk) {
        toast.error(`Approved, but publish failed. The draft stays in the Approved state - you can publish manually. ${msg || ""}`.trim());
      } else {
        toast.error(msg || "Approve failed");
      }
      // Reload so the UI shows the current state (approved or still
      // awaiting_review) - lets the manager pick the next safe action.
      try { await load(); } catch (_e) { /* swallow - already toasted */ }
    } finally { setBusy(false); }
  }, [period_id, period, load, navigate, publishScope]);

  const revertToDraft = useCallback(async () => {
    if (!window.confirm("Move this back to draft so you can edit before publishing?")) return;
    setBusy(true);
    try {
      await api.post(`/drafts/rota/${period_id}/revert-to-draft`);
      toast.success("Moved back to draft.");
      await load();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Revert failed");
    } finally { setBusy(false); }
  }, [period_id, load]);

  const discard = useCallback(async () => {
    // Phase 2Z.ak - the confirmation surface is now a proper Dialog
    // (`discard-confirm-dialog`) which only opens after the manager
    // clicks the header button. The dialog's confirm button calls
    // this handler, so window.prompt is no longer needed. We still
    // record an optional reason via the audit row written by the
    // backend, but collecting that text mid-flow added friction and
    // managers were dismissing the prompt by mistake.
    setDiscardConfirmOpen(false);
    setBusy(true);
    try {
      await api.post(`/drafts/rota/${period_id}/discard`, { reason: null });
      // V1: Rota Style drafts should round-trip the user back to the
      // pattern so they can update it before generating again. Other
      // drafts still return to the list view.
      if (period?.source === "rota_style") {
        toast.success(
          "Draft discarded. Update your Rota Style before generating again.",
        );
        navigate("/app/rota-style");
      } else {
        toast.success("Draft discarded.");
        navigate("/app/drafts/rota");
      }
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Discard failed");
    } finally { setBusy(false); }
  }, [period_id, period, navigate]);

  const regenerate = useCallback(async () => {
    if (!window.confirm("Regenerate this draft? The current version will be replaced.")) return;
    setBusy(true);
    try {
      await api.post("/drafts/rota/run-now", {
        window_start: period?.window_start,
        confidence_floor: period?.confidence_floor || "strong_only",
        force_regenerate: true,
      });
      toast.success("Regenerated. Loading the new draft.");
      const { data: list } = await api.get("/drafts/rota");
      const latest = (list.periods || []).find(
        (p) => p.window_start === period?.window_start &&
               p.status === "awaiting_review");
      if (latest) {
        navigate(`/app/drafts/rota/${latest.period_id}`);
      } else {
        await load();
      }
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Regenerate failed");
    } finally { setBusy(false); }
  }, [period, load, navigate]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-5 w-5 animate-spin text-[#4B5773]" />
      </div>
    );
  }
  if (!period) return null;

  // Phase 2Z.al.2 - Team filter is purely frontend. We filter the
  // cells once here and reuse the filtered array for both List view
  // and Grid view so the manager sees a consistent count + layout.
  // Cell-level `target_team_id` is the source of truth; legacy or
  // non-Rota-Style cells without that field fall back to the
  // suggested employee's home team.
  const teamIdsInPeriod = Array.from(new Set(
    (period.cells || []).map((c) =>
      c.target_team_id || empTeamIdById[c.suggested_employee_id] || null
    ).filter(Boolean)
  )).sort();
  const teamFilterActive = selectedTeamId !== "all";
  const filteredCells = teamFilterActive
    ? (period.cells || []).filter((c) => {
        const tid = c.target_team_id
          || empTeamIdById[c.suggested_employee_id]
          || null;
        return tid === selectedTeamId;
      })
    : (period.cells || []);
  const filteredPeriod = teamFilterActive
    ? { ...period, cells: filteredCells }
    : period;
  const cellsByDate = filteredCells.reduce((acc, c) => {
    (acc[c.target_date] ||= []).push(c);
    return acc;
  }, {});
  const dates = Object.keys(cellsByDate).sort();
  const status = period.status;
  const isRotaStyle = period.source === "rota_style";
  const pendingCellCount = (period.cells || []).filter(c => c.status === "pending_review").length;
  const approvedCellCount = (period.cells || []).filter(c => c.status === "approved_in_draft").length;
  const totalToPublish = approvedCellCount;
  const canPublishFromAwaiting = status === "awaiting_review" && approvedCellCount > 0;

  return (
    <div data-testid="drafts-rota-review-page">
      <Link to="/app/drafts/rota"
            className="inline-flex items-center text-sm text-[#4B5773] hover:text-[#0B1A36] mb-3">
        <ChevronLeft className="h-4 w-4 mr-1" /> Back to draft rotas
      </Link>

      <div className="flex items-end justify-between mb-4 flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-2xl font-bold text-[#0B1A36]"
                data-testid="rota-review-title">
              Week of {fmtDate(period.window_start)} - {fmtDate(period.window_end)}
            </h1>
            <span className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded border ${STATUS_TONES[status] || ""}`}
                  data-testid="rota-review-status">
              {status.replace(/_/g, " ")}
            </span>
          </div>
          <p className="text-sm text-[#4B5773] mt-1"
             data-testid="rota-review-summary">
            {period.manager_copy}
          </p>
        </div>

        {status === "awaiting_review" && (
          <div className="flex gap-2 flex-wrap">
            {!isRotaStyle && (
              <Button variant="outline" onClick={regenerate} disabled={busy}
                      data-testid="rota-regenerate-btn">
                <RefreshCw className="h-4 w-4 mr-2" /> Regenerate
              </Button>
            )}
            {isRotaStyle && (
              <Button variant="outline" onClick={editInRoster} disabled={busy}
                      data-testid="rota-edit-in-roster-btn">
                {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> :
                         <Pencil className="h-4 w-4 mr-2" />}
                Edit in roster draft
              </Button>
            )}
            {/* Phase 2Z.af - hide "Approve all" on Rota Style awaiting_review
                drafts: managers were getting pushed through "Approve all" ->
                approved page -> "Publish to live rota". The shortcut covers
                this in one action. The underlying approve endpoint is still
                reachable for automation + non-Rota-Style flows. */}
            {!isRotaStyle && pendingCellCount > 0 && (
              <Button variant="outline" onClick={approveDraft} disabled={busy}
                      data-testid="rota-approve-btn">
                {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> :
                         <CheckCircle2 className="h-4 w-4 mr-2" />}
                Approve all
              </Button>
            )}
            {/* Phase 2Z.af - shortcut is now also shown on Rota Style
                awaiting_review drafts. Same handler, same two endpoints,
                same safety: if publish fails after approve, draft stays
                in the safe approved state. */}
            {pendingCellCount > 0 && (
              <Button onClick={() => setApprovePublishConfirmOpen(true)} disabled={busy}
                      data-testid="rota-approve-and-publish-btn"
                      className="bg-emerald-600 hover:bg-emerald-700 text-white">
                {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> :
                         <CheckCircle2 className="h-4 w-4 mr-2" />}
                Approve and publish to live rota
              </Button>
            )}
            {!isRotaStyle && canPublishFromAwaiting && (
              <Button onClick={() => setPublishConfirmOpen(true)} disabled={busy}
                      data-testid="rota-publish-btn">
                {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> :
                         <Send className="h-4 w-4 mr-2" />}
                Publish to live rota
              </Button>
            )}
            {/* Phase 2Z.ak - Discard draft is the safety hatch for a
                draft that should not have been generated. Visible on
                BOTH Rota Style and non-Rota-Style awaiting_review
                drafts. Not shown on approved drafts (we do not want
                to silently roll back already-approved cells - the
                manager should use Move back to draft + Discard if
                they really want that). Uses an existing endpoint;
                no new backend route. */}
            <Button variant="outline"
                    onClick={() => setDiscardConfirmOpen(true)}
                    disabled={busy}
                    data-testid="rota-discard-btn"
                    className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200">
              <Trash2 className="h-4 w-4 mr-2" />
              Discard draft
            </Button>
          </div>
        )}
        {status === "approved" && (
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" onClick={revertToDraft} disabled={busy}
                    data-testid="rota-revert-btn">
              <Undo2 className="h-4 w-4 mr-2" /> Move back to draft
            </Button>
            <Button onClick={() => setPublishConfirmOpen(true)} disabled={busy}
                    data-testid="rota-publish-btn">
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> :
                       <Send className="h-4 w-4 mr-2" />}
              Publish to live rota
            </Button>
          </div>
        )}
      </div>

      {isRotaStyle && status === "awaiting_review" && (
        <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50 p-3 flex items-start gap-2"
             data-testid="rota-style-helper-banner">
          <AlertTriangle className="h-4 w-4 text-slate-700 mt-0.5 shrink-0" />
          <p className="text-xs text-slate-800">
            If this draft is not right, edit it before approving.
          </p>
        </div>
      )}

      {period.needs_review && (
        <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-4"
             data-testid="rota-needs-review-banner">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex-1 min-w-0">
              <h3 className="font-medium text-amber-900 mb-1">
                This draft needs your attention
              </h3>
              <p className="text-xs text-amber-800">
                {period.needs_review_summary?.auto_refreshed > 0 && (
                  <>NextRota updated <strong>{period.needs_review_summary.auto_refreshed} shift{period.needs_review_summary.auto_refreshed === 1 ? "" : "s"}</strong> after a recent leave change. </>
                )}
                {period.needs_review_summary?.needs_attention > 0 && (
                  <><strong>{period.needs_review_summary.needs_attention} shift{period.needs_review_summary.needs_attention === 1 ? "" : "s"}</strong> could not be reassigned automatically. </>
                )}
                {period.needs_review_summary?.manager_edits_skipped > 0 && (
                  <><strong>{period.needs_review_summary.manager_edits_skipped} of your edited shift{period.needs_review_summary.manager_edits_skipped === 1 ? "" : "s"}</strong> were preserved as-is. </>
                )}
                Review the changes below and confirm when you are happy.
              </p>
            </div>
            <Button size="sm" variant="outline" onClick={async () => {
              try {
                await api.post(`/drafts/rota/${period_id}/acknowledge-needs-review`,
                                 { confirm: true });
                toast.success("Acknowledged.");
                await load();
              } catch (err) {
                toast.error(formatError(err.response?.data?.detail) || "Acknowledge failed");
              }
            }} data-testid="rota-acknowledge-btn">
              <CheckCircle2 className="h-4 w-4 mr-2" /> Acknowledge changes
            </Button>
          </div>
        </div>
      )}

      {!period.needs_review && period.last_leave_change_summary && (
        <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 p-3 flex items-start gap-2"
             data-testid="rota-silent-refresh-banner">
          <RefreshCw className="h-4 w-4 text-blue-700 mt-0.5 shrink-0" />
          <p className="text-xs text-blue-900">
            NextRota updated <strong>{period.last_leave_change_summary.refreshes?.length || 0} shift{(period.last_leave_change_summary.refreshes?.length || 0) === 1 ? "" : "s"}</strong> after a recent leave change. Look for the <em>Updated by leave change</em> chip below.
          </p>
        </div>
      )}

      {status === "awaiting_review" && approvedCellCount > 0 && (
        <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 p-3 flex items-start gap-2"
             data-testid="rota-some-approved-banner">
          <CheckCircle2 className="h-4 w-4 text-blue-700 mt-0.5 shrink-0" />
          <div className="text-xs text-blue-900">
            <strong>{approvedCellCount} shift{approvedCellCount === 1 ? " is" : "s are"} approved into this draft</strong>
            {pendingCellCount > 0 ? <> · {pendingCellCount} still pending review.</> : <>.</>}
            {" "}Publish writes the approved shifts to the live rota. Pending shifts stay pending.
          </div>
        </div>
      )}

      {status === "approved" && (
        <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 p-3 flex items-start gap-2"
             data-testid="rota-approved-banner">
          <CheckCircle2 className="h-4 w-4 text-blue-700 mt-0.5 shrink-0" />
          <div className="text-xs text-blue-900">
            This draft is <strong>approved and ready to publish</strong>.
            Your team will not see these shifts until you publish them.
          </div>
        </div>
      )}

      {/* Phase 2Z.ah - awaiting-review automation banner. Only shown
          when the draft was generated by automation (triggered_by
          starts with "scheduler" or "system" - manual drafts have
          "user:..." and are never auto-approved per server.py L16380)
          AND auto-approve is enabled. Wording is approximate ("about
          N hours") because auto-approve settings are read live by the
          sweep so the exact moment can shift if the manager later
          updates the settings. Existing auto-publish banner on the
          approved-state page is untouched. */}
      {status === "awaiting_review" &&
        autoApprove?.enabled &&
        typeof period.triggered_by === "string" &&
        /^(scheduler|system)/.test(period.triggered_by) && (
        <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-3 flex items-start gap-2"
             data-testid="rota-auto-approve-banner">
          <AlertTriangle className="h-4 w-4 text-amber-900 mt-0.5 shrink-0" />
          <div className="text-xs text-amber-900">
            <strong>Automation is active.</strong>{" "}
            If no manager takes action, NextRota will auto-approve this
            draft in about{" "}
            <strong>
              {autoApprove.review_window_hours} hour
              {autoApprove.review_window_hours === 1 ? "" : "s"}
            </strong>
            {" "}from when it was generated.
            {autoPublish?.enabled && (
              <>
                {" "}If auto-publish is on, it will then follow your
                auto-publish settings after approval.
              </>
            )}
            {" "}Edit or approve any time before then.
          </div>
        </div>
      )}

      {status === "approved" && autoPublish?.enabled && !period.publish_cancelled && (
        <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-3 flex items-start justify-between gap-3 flex-wrap"
             data-testid="rota-auto-publish-banner">
          <div className="flex items-start gap-2 flex-1 min-w-0">
            <Send className="h-4 w-4 text-amber-900 mt-0.5 shrink-0" />
            <p className="text-xs text-amber-900">
              <strong>Auto-publish is on.</strong> NextRota will publish this
              rota to your team in <strong>{autoPublish.publish_cooling_off_hours} hour{autoPublish.publish_cooling_off_hours === 1 ? "" : "s"}</strong>
              {" "}from approval. A cancel link is in your email; you can
              also cancel here at any time during the cooling-off.
            </p>
          </div>
          <Button size="sm" variant="outline" onClick={cancelAutoPublish}
                  disabled={busy} data-testid="rota-cancel-auto-publish-btn">
            <XCircle className="h-4 w-4 mr-2" /> Cancel auto-publish
          </Button>
        </div>
      )}

      {status === "approved" && period.publish_cancelled && (
        <div className="mb-4 rounded-lg border border-slate-300 bg-slate-50 p-3 flex items-start gap-2"
             data-testid="rota-auto-publish-cancelled-banner">
          <XCircle className="h-4 w-4 text-slate-700 mt-0.5 shrink-0" />
          <p className="text-xs text-slate-800">
            Auto-publish for this rota was cancelled. The rota stays approved
            so you can edit it or publish manually when you are ready.
          </p>
        </div>
      )}

      {status === "published" && (
        <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 p-3 flex items-start gap-2"
             data-testid="rota-published-banner">
          <CheckCircle2 className="h-4 w-4 text-emerald-700 mt-0.5 shrink-0" />
          <div className="text-xs text-emerald-900">
            This draft was <strong>published to the live rota</strong>.
            Open the Roster page to see the shifts in context.
          </div>
        </div>
      )}

      {period.summary?.needs_attention > 0 && (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-3 flex items-start gap-2"
             data-testid="rota-needs-attention-banner">
          <AlertTriangle className="h-4 w-4 text-amber-700 mt-0.5 shrink-0" />
          <div className="text-xs text-amber-900">
            <strong>{period.summary.needs_attention} shift{period.summary.needs_attention !== 1 ? "s" : ""}</strong>
            {' '}could not be drafted automatically. These shifts are listed
            below and need a manager to assign someone.
          </div>
        </div>
      )}

      {/* View-mode toggle. Grid is the new default — it shows every
          employee × day at a glance and exposes per-day / per-employee
          Approve buttons. List keeps the original day-stacked view for
          long-form review or printing. */}
      {status === "awaiting_review" && (period.cells || []).length > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <div className="inline-flex rounded-md border border-slate-200 bg-white p-0.5 text-xs"
               data-testid="rota-view-toggle">
            <button type="button"
                    onClick={() => setViewMode("grid")}
                    className={`px-3 py-1 rounded ${viewMode === "grid"
                      ? "bg-slate-900 text-white" : "text-[#4B5773]"}`}
                    data-testid="rota-view-grid-btn">
              Grid
            </button>
            <button type="button"
                    onClick={() => setViewMode("list")}
                    className={`px-3 py-1 rounded ${viewMode === "list"
                      ? "bg-slate-900 text-white" : "text-[#4B5773]"}`}
                    data-testid="rota-view-list-btn">
              List
            </button>
          </div>
          {/* Phase 2Z.al.2 - Team filter. Rendered only when the
              draft actually spans more than one team so single-team
              drafts stay clutter-free. Filter is purely frontend -
              no extra fetch, no backend change. */}
          {teamIdsInPeriod.length > 1 && (
            <label className="inline-flex items-center gap-1.5 text-xs text-[#4B5773]">
              <span className="text-[10px] uppercase tracking-widest font-semibold">
                Team
              </span>
              <select
                value={selectedTeamId}
                onChange={(e) => setSelectedTeamId(e.target.value)}
                className="text-xs rounded-md border border-slate-200 bg-white px-2 py-1"
                data-testid="rota-team-filter">
                <option value="all">All teams</option>
                {teamIdsInPeriod.map((tid) => (
                  <option key={tid} value={tid}>
                    {teamNameById[tid] || tid}
                  </option>
                ))}
              </select>
              {teamFilterActive && (
                <span className="text-[10px] text-amber-700"
                      data-testid="rota-team-filter-active">
                  Showing {filteredCells.length} of {(period.cells || []).length}
                </span>
              )}
            </label>
          )}
        </div>
      )}

      {viewMode === "grid" && status === "awaiting_review" && (period.cells || []).length > 0 ? (
        <WeeklyGrid period={filteredPeriod}
                     busy={busy}
                     hideRowColApprove={isRotaStyle}
                     onApproveDay={(d) =>
                       approveSubset({ date: d }, fmtDate(d))}
                     onApproveEmployee={(emp) =>
                       approveSubset({ employee_id: emp.employee_id },
                                       emp.employee_name)} />
      ) : (
        <div className="space-y-4" data-testid="rota-list-view">
          {dates.map((d) => (
            <div key={d}
                 className="rounded-xl border border-amber-200 bg-white shadow-sm overflow-hidden"
                 data-testid={`rota-day-${d}`}>
              <div className="px-4 py-2 border-b border-amber-200 bg-amber-50 text-sm font-semibold text-amber-900 flex items-center justify-between">
                <span>{fmtDate(d)}</span>
                <span className="text-[10px] uppercase tracking-widest font-semibold text-amber-800">Pending review</span>
              </div>
              <div className="divide-y divide-amber-100">
                {/* Phase 2Z.al - group draft cells into Rota Style
                    blocks so the manager can see per-assignment
                    start/end times and durations the way the publish
                    path will actually write them. Cells that share
                    a `style_cell_id` form one block (visualised by a
                    subtle thin left bar). Non-Rota-Style cells fall
                    through as singleton blocks and render identically
                    to today. */}
                {groupCellsIntoBlocks(cellsByDate[d]).map((block, bi) => {
                  const timeline = computeAssignmentTimeline(block);
                  const isBlock = block.length > 1;
                  const blockKey = block[0]?.style_cell_id
                                    || block[0]?.draft_id
                                    || `b-${bi}`;
                  let assignmentIdx = 0;
                  return (
                    <div key={blockKey}
                         className={isBlock
                           ? "border-l-2 border-blue-300 pl-0.5"
                           : ""}
                         data-testid={isBlock
                           ? `rota-block-${blockKey}`
                           : undefined}>
                      {timeline.rows.map((row, ri) => {
                        if (row.kind === "break") {
                          // Phase 2Z.al - dedicated break row. Only
                          // shown when the block has a break. Exact
                          // timing only when break_start_time exists.
                          return (
                            <div key={`brk-${blockKey}-${ri}`}
                                 className="px-4 py-2 flex items-center gap-2 bg-slate-50/70 text-xs text-slate-700"
                                 data-testid={`rota-break-row-${blockKey}`}>
                              <Coffee className="h-3 w-3 text-slate-500 shrink-0" />
                              <span className="font-medium">Break</span>
                              {row.has_fixed_time && row.start && row.end ? (
                                <span className="font-mono"
                                      data-testid={`rota-break-window-${blockKey}`}>
                                  {row.start} to {row.end}
                                </span>
                              ) : (row.start && row.end) ? (
                                // Phase 2Z.al.1 - the helper computed
                                // an auto break placement (right after
                                // the first assignment). Show the
                                // times AND a small "(auto)" hint so
                                // managers see what publish will use.
                                <span className="font-mono"
                                      data-testid={`rota-break-auto-${blockKey}`}>
                                  {row.start} to {row.end}
                                  <span className="ml-1 text-[10px] text-slate-500 font-sans">
                                    (auto)
                                  </span>
                                </span>
                              ) : (
                                <span className="text-slate-500"
                                      data-testid={`rota-break-nofix-${blockKey}`}>
                                  no fixed time
                                </span>
                              )}
                              <span className="text-slate-500">
                                · {row.mins} min
                              </span>
                            </div>
                          );
                        }
                        const c = row.cell;
                        const isFirstAssignmentInBlock = assignmentIdx === 0;
                        assignmentIdx += 1;
                        return (
                  <div key={c.draft_id}
                       className="px-4 py-3 flex items-start justify-between gap-3 flex-wrap bg-amber-50/30 hover:bg-amber-50/60 transition-colors"
                       data-testid={`rota-cell-${c.draft_id}`}>
                    <div className="flex-1 min-w-0">
                      {isFirstAssignmentInBlock && (
                        <>
                          <div className="font-semibold text-[#0B1A36] text-sm">
                            {c.suggested_employee_name || c.suggested_employee_id}
                          </div>
                          {/* Phase 2Z.ae - skills hint under the name. No new
                              fetch - skillIds come from the load() employees
                              map, names from /skills loaded once. */}
                          <EmployeeSkillsLine
                            skillIds={empSkillIdsById[c.suggested_employee_id]}
                            skillNameById={skillNameById}
                            testidPrefix={`rota-cell-${c.draft_id}`}
                          />
                        </>
                      )}
                      <div className="flex items-center gap-1.5 flex-wrap mt-1.5">
                        {(c.policy_name || c.target_category_name) && (
                          <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded border bg-white text-[#1D4ED8] border-blue-200 font-semibold">
                            {c.policy_name || c.target_category_name}
                          </span>
                        )}
                        {c.target_team_name && (
                          <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded border bg-white text-[#0B1A36] border-slate-200">
                            {c.target_team_name}
                          </span>
                        )}
                        {/* Phase 2Z.al - per-assignment window
                            (NOT the whole-shift window). Falls back
                            to the cell's whole-shift window only when
                            the block has no usable timing data. */}
                        {(row.start && row.end) ? (
                          <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded border bg-white text-slate-700 border-slate-200 font-mono"
                                data-testid={`rota-cell-window-${c.draft_id}`}>
                            {row.start} to {row.end}
                          </span>
                        ) : (c.start_time || c.end_time) ? (
                          <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded border bg-white text-slate-700 border-slate-200 font-mono"
                                data-testid={`rota-cell-window-${c.draft_id}`}>
                            {c.start_time || "--"}-{c.end_time || "--"}
                          </span>
                        ) : null}
                        {/* Phase 2Z.al - explicit per-assignment
                            duration in "60 min (1h)" form so managers
                            can read the timeline at a glance. */}
                        {row.mins > 0 && (
                          <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded border bg-white text-slate-700 border-slate-200"
                                data-testid={`rota-cell-mins-${c.draft_id}`}>
                            {fmtMinutes(row.mins)}
                          </span>
                        )}
                        {/* Phase 2Z.al - the break is now rendered
                            as a dedicated row inside the block. The
                            old "Xm break" chip is suppressed when we
                            have a dedicated row (i.e. this is the
                            first cell of the block and the block has
                            a break). Singleton blocks keep the chip
                            for backwards compat. */}
                        {c.break_minutes && !isBlock ? (
                          <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded border bg-white text-slate-700 border-slate-200"
                                data-testid={`rota-cell-break-${c.draft_id}`}>
                            {c.break_minutes}m break
                          </span>
                        ) : null}
                        {c.confidence_band && (
                          <span className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded border ${BAND_TONES[c.confidence_band] || ""}`}>
                            {c.confidence_band}
                          </span>
                        )}
                        {c.leave_change_refresh_from_employee_id && (
                          <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded border bg-blue-50 text-blue-800 border-blue-200"
                                data-testid={`cell-updated-by-leave-${c.draft_id}`}
                                title={`Was: ${c.leave_change_refresh_from_employee_id}`}>
                            Updated by leave change
                          </span>
                        )}
                      </div>
                      {c.rationale_text && (
                        <p className="text-xs text-[#4B5773] mt-1.5">{c.rationale_text}</p>
                      )}
                    </div>
                    <span className={`text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-full border ${
                      CELL_STATUS_TONES[c.status] || "text-slate-600 bg-slate-50 border-slate-200"
                    }`}
                          data-testid={`rota-cell-status-${c.draft_id}`}>
                      {c.status.replace(/_/g, " ")}
                    </span>
                  </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {period.summary?.shifts_needing_attention?.length > 0 && (
        <div className="mt-6 rounded-lg border bg-white p-4">
          <h3 className="font-medium text-[#0B1A36] mb-2">
            Shifts that need a manager
          </h3>
          <ul className="text-sm text-[#4B5773] space-y-1">
            {period.summary.shifts_needing_attention.map((s, i) => (
              <li key={i}>
                {fmtDate(s.date)} - {s.policy_name || s.category_name}
                {s.shortfall ? ` (${s.shortfall} short)` : ""}
              </li>
            ))}
          </ul>
        </div>
      )}

      <Dialog open={publishConfirmOpen} onOpenChange={setPublishConfirmOpen}>
        <DialogContent data-testid="publish-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Publish to live rota?</DialogTitle>
            <DialogDescription>
              Publish {totalToPublish} shift{totalToPublish === 1 ? "" : "s"} to the
              live rota for the week of {fmtDate(period.window_start)} - {fmtDate(period.window_end)}?
              Your team will see these shifts as soon as you publish.
            </DialogDescription>
          </DialogHeader>
          {/* TP-RS.team.1 -- Workspace Admin selected-team publish.
              Default "all" keeps the legacy full-publish behaviour
              byte-stable. When a single team is picked, the request
              forwards ?team_id= and the existing TP-3 partial-publish
              branch fires (anti-overwrite guard, team-scoped emails,
              status stays draft for other teams until the full publish
              is invoked). Hidden for team_admin (their own partial-
              publish button lives elsewhere on the page) and when
              there is only one or zero teams to choose from. */}
          {isCompanyAdmin && Object.keys(teamNameById || {}).length > 0 && (
            <div
              className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 space-y-1.5"
              data-testid="publish-team-scope-picker"
            >
              <div className="text-[10px] uppercase tracking-widest text-[#94A3B8] font-semibold">
                Publish scope
              </div>
              <div className="flex flex-wrap gap-1.5">
                <button
                  type="button"
                  onClick={() => setPublishScope("all")}
                  className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${
                    publishScope === "all"
                      ? "bg-[#0B1A36] text-white border-[#0B1A36]"
                      : "bg-white text-[#0B1A36] border-slate-200 hover:bg-slate-50"
                  }`}
                  data-testid="publish-scope-all"
                >
                  All remaining teams
                </button>
                {Object.entries(teamNameById).map(([tid, tname]) => {
                  const already = (period?.published_team_ids || []).includes(tid);
                  return (
                    <button
                      key={tid}
                      type="button"
                      onClick={() => setPublishScope(tid)}
                      disabled={already}
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${
                        publishScope === tid
                          ? "bg-[#0B1A36] text-white border-[#0B1A36]"
                          : already
                            ? "bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed"
                            : "bg-white text-[#0B1A36] border-slate-200 hover:bg-slate-50"
                      }`}
                      data-testid={`publish-scope-team-${tid}`}
                      title={already ? "This team is already published." : ""}
                    >
                      {tname} only
                      {already && (
                        <span className="text-[10px] text-emerald-600">· published</span>
                      )}
                    </button>
                  );
                })}
              </div>
              <p className="text-[11px] text-[#4B5773] pt-0.5">
                {publishScope === "all"
                  ? "Publishes every team that has not yet been published this week."
                  : "Publishes only this team. Other teams stay as a draft. Only this team's employees will be emailed."}
              </p>
            </div>
          )}
          <DialogFooter className="gap-2">
            <DialogClose asChild>
              <Button variant="outline" data-testid="publish-cancel-btn">Cancel</Button>
            </DialogClose>
            <Button onClick={publishToLive} disabled={busy}
                    data-testid="publish-confirm-btn">
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> :
                       <Send className="h-4 w-4 mr-2" />}
              {publishScope === "all"
                ? "Publish to live rota"
                : `Publish ${teamNameById?.[publishScope] || "team"} only`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Phase 2Z.ae - confirmation for the Approve-and-publish shortcut.
          Same safety bar as the standalone Publish dialog: explicit
          confirm, clear count + week label, and unique testids so the
          two existing flows remain regression-locked. */}
      <Dialog open={approvePublishConfirmOpen} onOpenChange={setApprovePublishConfirmOpen}>
        <DialogContent data-testid="approve-and-publish-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Approve and publish to live rota?</DialogTitle>
            <DialogDescription>
              Approve {pendingCellCount} pending shift{pendingCellCount === 1 ? "" : "s"} and then publish the rota for the week of {fmtDate(period.window_start)} - {fmtDate(period.window_end)} to your team. Your team will see these shifts as soon as you confirm.
            </DialogDescription>
          </DialogHeader>
          {/* TP-RS.team.1 - selected-team publish also available on
              the Approve-and-publish shortcut. Mirrors the picker on
              the standalone Publish dialog so Workspace Admin can
              choose "All remaining teams" (legacy default) or a
              single team. Hidden for team_admin and when there is
              only one or zero teams. */}
          {isCompanyAdmin && Object.keys(teamNameById || {}).length > 0 && (
            <div
              className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 space-y-1.5"
              data-testid="approve-and-publish-team-scope-picker"
            >
              <div className="text-[10px] uppercase tracking-widest text-[#94A3B8] font-semibold">
                Publish scope
              </div>
              <div className="flex flex-wrap gap-1.5">
                <button
                  type="button"
                  onClick={() => setPublishScope("all")}
                  className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${
                    publishScope === "all"
                      ? "bg-[#0B1A36] text-white border-[#0B1A36]"
                      : "bg-white text-[#0B1A36] border-slate-200 hover:bg-slate-50"
                  }`}
                  data-testid="approve-and-publish-scope-all"
                >
                  All remaining teams
                </button>
                {Object.entries(teamNameById).map(([tid, tname]) => {
                  const already = (period?.published_team_ids || []).includes(tid);
                  return (
                    <button
                      key={tid}
                      type="button"
                      onClick={() => setPublishScope(tid)}
                      disabled={already}
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${
                        publishScope === tid
                          ? "bg-[#0B1A36] text-white border-[#0B1A36]"
                          : already
                            ? "bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed"
                            : "bg-white text-[#0B1A36] border-slate-200 hover:bg-slate-50"
                      }`}
                      data-testid={`approve-and-publish-scope-team-${tid}`}
                      title={already ? "This team is already published." : ""}
                    >
                      {tname} only
                      {already && (
                        <span className="text-[10px] text-emerald-600">· published</span>
                      )}
                    </button>
                  );
                })}
              </div>
              <p className="text-[11px] text-[#4B5773] pt-0.5">
                {publishScope === "all"
                  ? "Approves everything, then publishes every team that has not yet been published this week."
                  : "Approves everything, then publishes only this team. Other teams stay as a draft. Only this team's employees will be emailed."}
              </p>
            </div>
          )}
          <DialogFooter className="gap-2">
            <DialogClose asChild>
              <Button variant="outline" data-testid="approve-and-publish-cancel-btn">Cancel</Button>
            </DialogClose>
            <Button onClick={approveAndPublish} disabled={busy}
                    data-testid="approve-and-publish-confirm-btn"
                    className="bg-emerald-600 hover:bg-emerald-700 text-white">
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> :
                       <Send className="h-4 w-4 mr-2" />}
              {publishScope === "all"
                ? "Approve and publish"
                : `Approve and publish ${teamNameById?.[publishScope] || "team"} only`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Phase 2Z.ak - Discard draft confirmation dialog. Uses the
          existing /drafts/rota/{id}/discard endpoint (no new route).
          The exact body copy is approved by the user and reflects
          the backend's verified guarantees:
            - the pending draft is removed (status -> 'discarded')
            - published rota is not touched (live allocations stay)
            - automation can regenerate later because 'discarded' is
              not in the `_existing_period_for_window` gate. */}
      <Dialog open={discardConfirmOpen} onOpenChange={setDiscardConfirmOpen}>
        <DialogContent data-testid="discard-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Discard this draft?</DialogTitle>
            <DialogDescription>
              This will remove the pending draft for this week. It will not
              change any published rota. Automation can generate a new draft
              later if the week is still eligible.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <DialogClose asChild>
              <Button variant="outline" data-testid="discard-cancel-btn">
                Cancel
              </Button>
            </DialogClose>
            <Button onClick={discard} disabled={busy}
                    data-testid="discard-confirm-btn"
                    className="bg-red-600 hover:bg-red-700 text-white">
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> :
                       <Trash2 className="h-4 w-4 mr-2" />}
              Discard draft
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// Weekly grid: rows = employees, columns = days. Each cell shows the
// drafted assignments (combined when multiple per day) plus a coloured
// status pill. Row and column headers carry one-click "Approve" buttons
// that target the new /approve-cells endpoint with a date or
// employee_id filter.
function WeeklyGrid({ period, busy, hideRowColApprove, onApproveDay, onApproveEmployee }) {
  const cells = period.cells || [];
  // Build sorted lists of unique dates and unique employees.
  const dateSet = new Set();
  const empMap = new Map();
  for (const c of cells) {
    dateSet.add(c.target_date);
    if (!empMap.has(c.suggested_employee_id)) {
      empMap.set(c.suggested_employee_id, {
        employee_id: c.suggested_employee_id,
        employee_name: c.suggested_employee_name || c.suggested_employee_id,
      });
    }
  }
  const dates = [...dateSet].sort();
  const employees = [...empMap.values()].sort((a, b) =>
    a.employee_name.localeCompare(b.employee_name));

  // Lookup: cells by (employee_id, date).
  const byKey = {};
  for (const c of cells) {
    const k = `${c.suggested_employee_id}|${c.target_date}`;
    (byKey[k] ||= []).push(c);
  }

  // Helpers — count pending cells per row / per column.
  const pendingForDate = (d) =>
    cells.filter((c) => c.target_date === d
                     && c.status === "pending_review").length;
  const pendingForEmp = (id) =>
    cells.filter((c) => c.suggested_employee_id === id
                     && c.status === "pending_review").length;

  return (
    <div className="overflow-auto rounded-lg border border-slate-200 bg-white"
         data-testid="rota-grid">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr>
            <th className="text-left text-xs text-[#4B5773] font-medium px-3 py-2 border-b border-slate-200 w-[200px] bg-slate-50">
              Employee
            </th>
            {dates.map((d) => {
              const pending = pendingForDate(d);
              return (
                <th key={d}
                    className="text-left text-xs px-3 py-2 border-b border-slate-200 bg-slate-50 align-top"
                    data-testid={`rota-grid-day-header-${d}`}>
                  <div className="text-[#0B1A36] font-medium">{fmtDate(d)}</div>
                  {!hideRowColApprove && pending > 0 && (
                    <Button size="sm" variant="outline"
                            disabled={busy}
                            onClick={() => onApproveDay(d)}
                            className="mt-1 h-6 px-2 text-[10px]"
                            data-testid={`rota-grid-approve-day-${d}`}>
                      <CheckCircle2 className="h-3 w-3 mr-1" />
                      Approve {pending}
                    </Button>
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {employees.map((emp) => {
            const pending = pendingForEmp(emp.employee_id);
            return (
              <tr key={emp.employee_id} className="border-b border-slate-100"
                  data-testid={`rota-grid-row-${emp.employee_id}`}>
                <td className="px-3 py-2 align-top">
                  <div className="font-medium text-[#0B1A36]">{emp.employee_name}</div>
                  {!hideRowColApprove && pending > 0 && (
                    <Button size="sm" variant="outline"
                            disabled={busy}
                            onClick={() => onApproveEmployee(emp)}
                            className="mt-1 h-6 px-2 text-[10px]"
                            data-testid={`rota-grid-approve-emp-${emp.employee_id}`}>
                      <CheckCircle2 className="h-3 w-3 mr-1" />
                      Approve {pending}
                    </Button>
                  )}
                </td>
                {dates.map((d) => {
                  const dayCells = byKey[`${emp.employee_id}|${d}`] || [];
                  // Phase 2Z.al.1 - Grid now renders per-assignment
                  // timing using the same helper as List view, so
                  // managers see the same calculated start/end and
                  // duration the publish path will write. No more
                  // "1/N" badges - they were technical and unhelpful.
                  const blocks = groupCellsIntoBlocks(dayCells);
                  return (
                    <td key={d}
                        className="px-2 py-2 border-l border-slate-100 align-top min-w-[160px]"
                        data-testid={`rota-grid-cell-${emp.employee_id}-${d}`}>
                      <div className="space-y-1">
                        {dayCells.length === 0 && (
                          <span className="text-[10px] text-slate-300"
                                data-testid={`rota-grid-empty-${emp.employee_id}-${d}`}>
                            ·
                          </span>
                        )}
                        {blocks.map((block, bi) => {
                          const timeline = computeAssignmentTimeline(block);
                          const blockKey = block[0]?.style_cell_id
                                            || block[0]?.draft_id
                                            || `b-${bi}`;
                          const first = block[0] || {};
                          const showWindow = Boolean(first.start_time
                                                       && first.end_time);
                          // Phase 2Z.al.2 - one compact block per
                          // (employee, day, style_cell_id) instead
                          // of separate chips per assignment. Manager
                          // sees the full shift window once at the
                          // top and an inline timeline underneath
                          // (assignment / break rows). Status of
                          // the first cell drives the container tone.
                          const tone = CELL_STATUS_TONES[first.status]
                            || "text-slate-600 bg-slate-50 border-slate-200";
                          return (
                            <div key={blockKey}
                                 className={`rounded border px-2 py-1 text-[11px] ${tone}`}
                                 data-testid={`rota-grid-block-${blockKey}`}>
                              {showWindow && (
                                <div className="font-semibold leading-tight font-mono"
                                     data-testid={`rota-grid-block-window-${blockKey}`}>
                                  {first.start_time} to {first.end_time}
                                </div>
                              )}
                              <div className="mt-0.5 space-y-0.5">
                                {timeline.rows.map((row, ri) => {
                                  if (row.kind === "break") {
                                    return (
                                      <div key={`brk-${blockKey}-${ri}`}
                                           className="text-[10px] text-slate-700 flex items-center gap-1"
                                           data-testid={`rota-grid-break-${blockKey}`}>
                                        <Coffee className="h-2.5 w-2.5 shrink-0 opacity-70" />
                                        <span className="font-medium">Break</span>
                                        {row.has_fixed_time && row.start && row.end ? (
                                          <span className="font-mono">
                                            {row.start} to {row.end}
                                          </span>
                                        ) : (row.start && row.end) ? (
                                          <span className="font-mono"
                                                data-testid={`rota-grid-break-auto-${blockKey}`}>
                                            {row.start} to {row.end}
                                            <span className="ml-0.5 text-slate-500 font-sans">
                                              auto
                                            </span>
                                          </span>
                                        ) : (
                                          <span className="text-slate-500"
                                                data-testid={`rota-grid-break-nofix-${blockKey}`}>
                                            no fixed time
                                          </span>
                                        )}
                                        <span className="text-slate-500 ml-auto">
                                          {row.mins}m
                                        </span>
                                      </div>
                                    );
                                  }
                                  const c = row.cell;
                                  return (
                                    <div key={c.draft_id}
                                         className="text-[10px] flex items-center gap-1"
                                         data-testid={`rota-grid-chip-${c.draft_id}`}>
                                      <span className="font-medium truncate flex-1">
                                        {row.label}
                                      </span>
                                      {(row.start && row.end) ? (
                                        <span className="font-mono opacity-80"
                                              data-testid={`rota-grid-chip-window-${c.draft_id}`}>
                                          {row.start} to {row.end}
                                        </span>
                                      ) : (c.start_time || c.end_time) ? (
                                        <span className="font-mono opacity-80">
                                          {c.start_time || "--"}-{c.end_time || "--"}
                                        </span>
                                      ) : null}
                                      {row.mins > 0 && (
                                        <span className="opacity-80 ml-auto"
                                              data-testid={`rota-grid-chip-mins-${c.draft_id}`}>
                                          {row.mins}m
                                        </span>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                              <div className="text-[9px] uppercase tracking-wide opacity-75 mt-0.5">
                                {(first.status || "").replace(/_/g, " ")}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
